/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Module Pricing Service - SuperAdmin Pricing Management
 * ============================================================================
 *
 * Service layer for SuperAdmin module pricing management endpoints.
 * Provides CRUD operations for pricing tiers and volume discounts.
 *
 * ============================================================================
 */

import { get, post, put, del } from '@/services/api/client';
import { ApiResponse } from '@/services/api/types';
import {
  ModulePricingTier,
  VolumeDiscountRule,
  CreateModulePricingTierRequest,
  UpdateModulePricingTierRequest,
  CreateVolumeDiscountRequest,
  SubscriptionAnalytics,
} from '@/types/superadmin.types';

const BASE_URL = '/superadmin/pricing';

/**
 * Module Pricing Service
 *
 * SuperAdmin APIs for managing module pricing tiers and discounts.
 */
export const modulePricingService = {
  // ========================================================================
  // Module Pricing Tiers
  // ========================================================================

  /**
   * Get all pricing tiers (including inactive).
   */
  async getAllTiers(moduleCode?: string): Promise<ModulePricingTier[]> {
    const params = moduleCode ? `?moduleCode=${moduleCode}` : '';
    const response = await get<ApiResponse<ModulePricingTier[]>>(`${BASE_URL}/tiers${params}`);
    return response.data;
  },

  /**
   * Get a specific pricing tier by module and tier code.
   */
  async getTier(moduleCode: string, tierCode: string): Promise<ModulePricingTier> {
    const response = await get<ApiResponse<ModulePricingTier>>(
      `${BASE_URL}/tiers/${moduleCode}/${tierCode}`
    );
    return response.data;
  },

  /**
   * Create a new pricing tier.
   */
  async createTier(data: CreateModulePricingTierRequest): Promise<ModulePricingTier> {
    const response = await post<ApiResponse<ModulePricingTier>>(`${BASE_URL}/tiers`, data);
    return response.data;
  },

  /**
   * Update an existing pricing tier.
   */
  async updateTier(id: string, data: UpdateModulePricingTierRequest): Promise<ModulePricingTier> {
    const response = await put<ApiResponse<ModulePricingTier>>(`${BASE_URL}/tiers/${id}`, data);
    return response.data;
  },

  /**
   * Activate a pricing tier.
   */
  async activateTier(id: string): Promise<ModulePricingTier> {
    const response = await post<ApiResponse<ModulePricingTier>>(`${BASE_URL}/tiers/${id}/activate`);
    return response.data;
  },

  /**
   * Deactivate a pricing tier.
   */
  async deactivateTier(id: string): Promise<ModulePricingTier> {
    const response = await post<ApiResponse<ModulePricingTier>>(`${BASE_URL}/tiers/${id}/deactivate`);
    return response.data;
  },

  /**
   * Delete a pricing tier.
   */
  async deleteTier(id: string): Promise<void> {
    await del<ApiResponse<void>>(`${BASE_URL}/tiers/${id}`);
  },

  // ========================================================================
  // Volume Discount Rules
  // ========================================================================

  /**
   * Get all volume discount rules.
   */
  async getDiscountRules(): Promise<VolumeDiscountRule[]> {
    const response = await get<ApiResponse<VolumeDiscountRule[]>>(`${BASE_URL}/discounts`);
    return response.data;
  },

  /**
   * Create a new volume discount rule.
   */
  async createDiscountRule(data: CreateVolumeDiscountRequest): Promise<VolumeDiscountRule> {
    const response = await post<ApiResponse<VolumeDiscountRule>>(`${BASE_URL}/discounts`, data);
    return response.data;
  },

  /**
   * Update a volume discount rule.
   */
  async updateDiscountRule(id: string, data: CreateVolumeDiscountRequest): Promise<VolumeDiscountRule> {
    const response = await put<ApiResponse<VolumeDiscountRule>>(`${BASE_URL}/discounts/${id}`, data);
    return response.data;
  },

  /**
   * Delete a volume discount rule.
   */
  async deleteDiscountRule(id: string): Promise<void> {
    await del<ApiResponse<void>>(`${BASE_URL}/discounts/${id}`);
  },

  // ========================================================================
  // Module Codes
  // ========================================================================

  /**
   * Get all module codes for dropdown selection.
   */
  async getModuleCodes(): Promise<string[]> {
    const response = await get<ApiResponse<string[]>>(`${BASE_URL}/modules`);
    return response.data;
  },

  // ========================================================================
  // Analytics & Reporting
  // ========================================================================

  /**
   * Get subscription analytics.
   */
  async getAnalytics(): Promise<SubscriptionAnalytics> {
    const response = await get<ApiResponse<SubscriptionAnalytics>>(`${BASE_URL}/analytics`);
    return response.data;
  },

  /**
   * Get total MRR (Monthly Recurring Revenue).
   */
  async getTotalMrr(): Promise<number> {
    const response = await get<ApiResponse<number>>(`${BASE_URL}/analytics/mrr`);
    return response.data;
  },
};

export default modulePricingService;
